// 帳號編輯大 modal（config-account-v-2「新增帳號／修改帳號」＝legacy `#dialogModal`）。
//
// legacy 流程（行號全部親驗，2026-09-22；詳 docs/plans/2026-09-22-batch10-account-modal-progress.md §1）：
//   - showModal(id, rowObj)（accountV2.jsp:186-216）：標題「新增帳號」／「修改帳號」（:189/:197）、
//     reset 六表單、預設停在員工分頁（:192 觸發 tabli-employee click；進場預設同 :122）。
//   - saveAccount()（:419-487）：required 檢核不過 → `欄位檢核錯誤!!`（:429，!!＝0021×2）→
//     POST config/accountV2/saveAccountAjax（:440，multipart）→ 成功：desc＋（新增時）
//     ` 並請繼續新增這個帳號的部門.`（:460，.＝002E）、**modal 不關**、照片刷新（:464-468）。
//   - 關閉：hidden.bs.modal → `if (!isInSetRole) doQuery()`（:442-448）——子 modal（證照/角色設定）
//     開著時不 reload。React 鏡像：只有主 modal 關閉才 reload（子 modal 疊放在上，進度檔 D7）。
//   - 6 分頁（:833-838）：員工資料/個人資料/證照/學籍資料/其他/部門職稱；
//     部門職稱 click → departmentTab.showDeptList()（:838）；證照清單在切頁時直接渲染（showList 鏡像）。
//
// 雛形資料策略（進度檔 D3~D6，照 RolePermissionModal 先例）：
//   - 存檔走既有 `dataSource.create/update('config-account-v-2',…)`（D-1 五個 op 之內）；
//     部門職稱/證照/角色設定以 `profileList`／`certifications` 存列上（種子＋update 存回）。
//   - 真呼叫 DataSource 的按鈕才帶 data-effect（C5-j）。
//   - getPhoto／download 無真檔案服務：照片與證照正反面預覽以 placeholder 呈現【雛形值待實機校準】。
//   - 「其他」分頁的顯示由 controller model attribute `showAccountTabOther` 控制（:837），
//     雛形取「顯示」【雛形值待實機校準】。
//   - 表格欄位鏡像：legacy doQuery 從 profileList 衍生部門/職稱/類別/角色欄（:345-383），
//     存檔時把衍生值寫回列上對應欄（person＝姓名、id2＝員工編號、unit/text3/category/category2）；
//     **id（登入帳號欄）是列鍵不可寫**，登入帳號欄在雛形仍為 fixture 值（差異記進度檔）。
//
// 【證據等級】§7 第 3 級（原始碼）。文案 codepoint 表見進度檔 §1.7。

import { useState, type Dispatch, type ReactElement, type SetStateAction } from 'react';
import { Button } from '@/components/base/Button';
import { PasswordInput } from '@/components/base/PasswordInput';
import { Select } from '@/components/base/Select';
import { Table, type TableRow } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { MockDataError, resolveLegacyMessage, useDataSource } from '@/mocks';
import {
  ACCOUNT_CERTIFICATE_TYPE_OPTIONS,
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_JOB_TITLE_OPTIONS,
  ACCOUNT_JOB_TYPE_OPTIONS,
  ACCOUNT_OFFICE_LOCATION_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
} from '@/mocks/resource-rules';
import type { Row } from '@/mocks/types';
import { toUiKey } from '@/pages/templates/shared';

/** 分頁 key（對照 legacy tab id）。 */
type AccountTab = 'employee' | 'personal' | 'certification' | 'school' | 'other' | 'deptpartment';

/** 員工分頁欄位（accountV2tab_employee.jsp，8 欄）。 */
interface EmployeeFormState {
  officeMobile: string;
  officeTel: string;
  officeCode: string;
  extNo: string;
  appCode: string;
  officEmail: string;
  employeeId: string;
  onBoardDateString: string;
}

/** 個人分頁欄位（accountV2tab_personal.jsp，12 欄）。 */
interface PersonalFormState {
  countrycode: string;
  countryId: string;
  gender: string;
  military: string;
  birthdayString: string;
  bloodType: string;
  postalCode: string;
  address: string;
  eatingHabits: string;
  telephone: string;
  mobile: string;
  schoolEmail: string;
}

/** 學籍分頁欄位（accountV2tab_school.jsp，5 欄）。 */
interface SchoolFormState {
  school: string;
  schoolAnnual: string;
  schoolId: string;
  teacher: string;
  skill: string;
}

/** 其他分頁欄位（accountV2tab_other.jsp，6 欄）。 */
interface OtherFormState {
  typeName: string;
  typeCode: string;
  licenseLevel: string;
  drugLicense: string;
  licenseNot5: string;
  officeLocation: string;
}

/** 部門職稱表單欄位（newDepartmentForm，4 欄）。 */
interface DeptFormState {
  organizationId: string;
  jobTypeId: string;
  departmentId: string;
  jobTitleId: string;
}

/** 表單欄位 setter（keyof 型別安全）。 */
function setFormValue<T>(setter: Dispatch<SetStateAction<T>>, key: keyof T): (next: string) => void {
  return (next) => {
    setter((current) => ({ ...current, [key]: next }) as T);
  };
}

export interface AccountV2DialogModalProps {
  readonly pageId: string;
  /** 'create'＝新增（legacy showModal(-1,0)）；'edit'＝修改（showModal(row.id, row)）。 */
  readonly mode: 'create' | 'edit';
  /** 修改時的列（legacy currentRow；profileList／certifications 種子掛在列上）。 */
  readonly row: Row | null;
  /** 關閉主 modal（呼叫端 setEditing(null)）。 */
  readonly onClose: () => void;
  /** 主 modal 關閉後刷新清單（legacy hidden.bs.modal → doQuery，:442-448）。 */
  readonly onClosed: () => void;
}

/** 列上的部門職稱清單（legacy getAllAccountListAjax 回的 profileList）。 */
function profilesOf(row: Row | null): readonly Row[] {
  const raw = row?.['profileList'];
  return Array.isArray(raw) ? (raw as readonly Row[]) : [];
}

/** 列上的證照清單（legacy config/certification/getCertificationListAjax 的 list，存列上鏡像）。 */
function certificationsOf(row: Row | null): readonly Row[] {
  const raw = row?.['certifications'];
  return Array.isArray(raw) ? (raw as readonly Row[]) : [];
}

const ACCOUNT_TABS: readonly { key: AccountTab; label: string }[] = [
  { key: 'employee', label: '員工資料' }, // accountV2.jsp:833
  { key: 'personal', label: '個人資料' }, // :834
  { key: 'certification', label: '證照' }, // :835
  { key: 'school', label: '學籍資料' }, // :836
  { key: 'other', label: '其他' }, // :837（showAccountTabOther；雛形恆顯示，見檔頭）
  { key: 'deptpartment', label: '部門職稱' }, // :838（legacy 檔名拼字 quirk 照實）
];

export function AccountV2DialogModal({
  pageId,
  mode,
  row,
  onClose,
  onClosed,
}: AccountV2DialogModalProps): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  /** 主 modal 關閉＝close＋reload（legacy hidden.bs.modal 一次觸發，:442-448）。 */
  const closeMainModal = () => {
    onClose();
    onClosed();
  };

  // ------------------------------------------------------------------
  // 表單值。修改模式帶入列上欄位（legacy setFormValueByJSONObject，:199-206；
  // mock 列只有表格欄位集，表單專屬欄位無種子值 → 空白【雛形值待實機校準】）。
  // 欄位 key 對照表格欄：person＝姓名、id＝登入帳號（**同時是列鍵，不可寫**）、id2＝員工編號。
  // ------------------------------------------------------------------
  const [name, setName] = useState(mode === 'edit' ? String(row?.['person'] ?? '') : '');
  const [englishName, setEnglishName] = useState('');
  const [loginName, setLoginName] = useState(
    mode === 'edit' ? String(row?.['loginName'] ?? row?.['id'] ?? '') : '',
  );
  const [loginPin, setLoginPin] = useState('');
  const [loginPinAgain, setLoginPinAgain] = useState('');

  const [employeeForm, setEmployeeForm] = useState<EmployeeFormState>(() => ({
    officeMobile: '',
    officeTel: '',
    officeCode: '',
    extNo: '',
    appCode: '',
    officEmail: '',
    employeeId: mode === 'edit' ? String(row?.['id2'] ?? '') : '',
    onBoardDateString: '',
  }));
  const [personalForm, setPersonalForm] = useState<PersonalFormState>(() => ({
    countrycode: '1', // personal.jsp:66-69 預設中華民國
    countryId: '',
    gender: '',
    military: '',
    birthdayString: '',
    bloodType: '',
    postalCode: '',
    address: '',
    eatingHabits: '',
    telephone: '',
    mobile: '',
    schoolEmail: '',
  }));
  const [schoolForm, setSchoolForm] = useState<SchoolFormState>(() => ({
    school: '',
    schoolAnnual: '',
    schoolId: '',
    teacher: '',
    skill: '',
  }));
  const [otherForm, setOtherForm] = useState<OtherFormState>(() => ({
    typeName: '',
    typeCode: '',
    licenseLevel: '',
    drugLicense: '',
    licenseNot5: '', // other.jsp:224 input id=licenseNo 但 name=licenseNot5（legacy quirk 照實）
    officeLocation: '',
  }));

  /** 帳號 id（legacy idTemp／#accountForm #id）：新增＝'0'，存檔成功後回填新列 id。 */
  const [accountId, setAccountId] = useState(mode === 'edit' ? String(row?.id ?? '') : '0');
  const [busy, setBusy] = useState(false);

  // ------------------------------------------------------------------
  // 分頁與子資料
  // ------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState<AccountTab>('employee'); // 預設員工（:122/:192）
  const [profiles, setProfiles] = useState<readonly Row[]>(profilesOf(row));
  const [certs, setCerts] = useState<readonly Row[]>(certificationsOf(row));

  // 部門職稱表單（legacy newDepartmentForm；select 選項＝種子池，級聯簡化為平列【雛形值待實機校準】）
  const [deptForm, setDeptForm] = useState<DeptFormState>(() => ({
    organizationId: '',
    jobTypeId: '',
    departmentId: '',
    jobTitleId: '',
  }));
  const [deptProfileId, setDeptProfileId] = useState('0');
  const [deptShowChange, setDeptShowChange] = useState(false);
  // legacy：showDeptList 時 currentRow==null → 連「新增部門設定」都 hide（deptpartment.jsp:45-48）；
  // 新增存檔後 currentRow 仍是 null → 重開 modal 前按鈕不出現（照實鏡像，D4）。
  const showDeptNew = mode === 'edit' && !deptShowChange;

  // 證照 modal（legacy certificationSettingModal）
  const [certModal, setCertModal] = useState<
    | null
    | {
        id: string;
        type: string;
        name: string;
        regisDate: string;
        expireDate: string;
        memo: string;
        typeInvalid: boolean;
        nameInvalid: boolean;
      }
  >(null);

  // 角色設定 modal（legacy roleSettingModal）
  const [roleModal, setRoleModal] = useState<
    | null
    | { profileId: string; checked: ReadonlySet<string> }
  >(null);
  const [roles, setRoles] = useState<readonly { id: string; name: string }[]>([]);

  const alertOrGeneric = async (cause: unknown) => {
    await dialogs.alert(cause instanceof MockDataError ? cause.message : t('error.generic'));
  };

  const saveSuccessText = () =>
    resolveLegacyMessage(pageId, 'success')?.text ?? t('state.saveSuccess');

  // ------------------------------------------------------------------
  // 存檔（legacy saveAccount，accountV2.jsp:419-487）
  // ------------------------------------------------------------------
  const saveAccount = () => {
    void (async () => {
      // legacy `$("#accountForm").valid()`：required＝中文姓名/登入帳號（:778/:789 的 required 屬性）；
      // 不過 → showAlertDialog('欄位檢核錯誤!!')（:429）。
      if (name.trim() === '' || loginName.trim() === '') {
        await dialogs.alert('欄位檢核錯誤!!');
        return;
      }
      const isNew = accountId === '0'; // :431（id=='0'）
      // legacy 送出欄位＝accountForm＋四分頁表單的 name（:421-458）；雛形以列上的 form 欄位合併。
      const values: Row = {
        name,
        englishName,
        loginName,
        loginPin,
        loginPin_again: loginPinAgain,
        ...employeeForm,
        ...personalForm,
        ...schoolForm,
        ...otherForm,
        // 表格欄位鏡像（legacy doQuery 從 profileList 衍生欄位，:345-383）。
        person: name,
        id2: employeeForm.employeeId,
      };
      const firstProfile = profiles[0];
      if (firstProfile !== undefined) {
        values.unit = firstProfile['departmentName'];
        values.text3 = firstProfile['jobTitleName'];
        values.category = firstProfile['jobTypeName'];
        values.category2 = [
          ...new Set(
            profiles.flatMap((pf) =>
              (Array.isArray(pf['permissionProfileRoleList'])
                ? (pf['permissionProfileRoleList'] as readonly Row[])
                : []
              ).map((role) => String(role['roleName'] ?? '')),
            ),
          ),
        ].join('、');
      }
      setBusy(true);
      try {
        const saved =
          accountId === '0'
            ? await dialogs.processing.wrap(dataSource.create(pageId, values))
            : await dialogs.processing.wrap(dataSource.update(pageId, accountId, values));
        setAccountId(String(saved['id'] ?? accountId)); // :446-456（三表單 accountId 回填的鏡像）
        // legacy 成功訊息＝response.desc（後端文字）＋新增時後綴（:459-461，前導空格照實）；
        // 雛形 desc 源用通用 success 訊息（與 CrudListPage persist 同源），後綴逐字組字。
        const base = saveSuccessText();
        await dialogs.info(isNew ? `${base} 並請繼續新增這個帳號的部門.` : base);
        // legacy 存檔後 modal 不關（:452-471 無 close）；照片刷新在雛形為 placeholder 無作用。
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  // ------------------------------------------------------------------
  // 部門職稱（legacy departmentTab，accountV2tab_deptpartment.jsp）
  // ------------------------------------------------------------------
  const resetDeptPanel = () => {
    setDeptProfileId('0'); // :119
    setDeptShowChange(false); // :120-122
    setDeptForm({ organizationId: '', jobTypeId: '', departmentId: '', jobTitleId: '' });
  };

  /** 切到部門職稱分頁＝legacy showDeptList（:38-50）。 */
  const showDeptList = () => {
    setDeptProfileId('0');
    setDeptShowChange(false);
    setDeptForm({ organizationId: '', jobTypeId: '', departmentId: '', jobTitleId: '' });
    setActiveTab('deptpartment');
  };

  /** 列「修改」＝legacy showChangePermissionProfile（:62-69）：回填四 select＋換鈕。 */
  const showChangePermissionProfile = (profile: Row) => {
    setDeptShowChange(true);
    setDeptProfileId(String(profile['id'] ?? ''));
    setDeptForm({
      organizationId: String(profile['organizationId'] ?? ''),
      jobTypeId: String(profile['jobTypeId'] ?? ''),
      departmentId: String(profile['departmentId'] ?? ''),
      jobTitleId: String(profile['jobTitleId'] ?? ''),
    });
  };

  /** 存檔／修改部門設定（legacy savePermissionProfile，:91-131）。fromChangeButton＝id==-1 沿用 profileId。 */
  const saveDeptProfile = (fromChangeButton: boolean) => {
    void (async () => {
      if (accountId === '0') {
        // :93（FF0C）
        await dialogs.alert('請先新增人員資料並存檔後，再維護部門職稱');
        return;
      }
      const targetProfileId = fromChangeButton ? deptProfileId : '0'; // :95-97
      // legacy 驗證 isEmpty || <=0（:99-110）；雛形選項值非數字，檢查空值即未選。
      if (deptForm.departmentId.trim() === '') {
        await dialogs.alert('未選擇部門!'); // :100（0021）
        return;
      }
      if (deptForm.jobTypeId.trim() === '') {
        await dialogs.alert('未選擇訓練類別!'); // :104（0021）
        return;
      }
      if (deptForm.jobTitleId.trim() === '') {
        await dialogs.alert('未選擇職稱!'); // :108（0021）
        return;
      }
      const nameOf = (pool: readonly { value: string; label: string }[], value: string) =>
        pool.find((option) => option.value === value)?.label ?? '';
      const base: Row = {
        organizationId: deptForm.organizationId,
        organizationName: nameOf(ACCOUNT_ORGANIZATION_OPTIONS, deptForm.organizationId),
        jobTypeId: deptForm.jobTypeId,
        jobTypeName: nameOf(ACCOUNT_JOB_TYPE_OPTIONS, deptForm.jobTypeId),
        departmentId: deptForm.departmentId,
        departmentName: nameOf(ACCOUNT_DEPARTMENT_OPTIONS, deptForm.departmentId),
        jobTitleId: deptForm.jobTitleId,
        jobTitleName: nameOf(ACCOUNT_JOB_TITLE_OPTIONS, deptForm.jobTitleId),
      };
      let next: readonly Row[];
      if (targetProfileId !== '0') {
        next = profiles.map((pf) =>
          String(pf['id']) === targetProfileId
            ? { ...pf, ...base, permissionProfileRoleList: pf['permissionProfileRoleList'] }
            : pf,
        );
      } else {
        next = [
          ...profiles,
          { id: `profile-${profiles.length + 1}`, ...base, permissionProfileRoleList: [] },
        ];
      }
      setBusy(true);
      try {
        // legacy POST config/accountV2/savePermissionProfileAjax（:114）→ response.list 重繪；
        // 雛形：profileList 存列上（update 既有 op）。
        await dialogs.processing.wrap(dataSource.update(pageId, accountId, { profileList: next }));
        setProfiles(next);
        resetDeptPanel();
        await dialogs.info(saveSuccessText());
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  /** 刪除部門（legacy deletePermissionProfile，:71-89）。 */
  const deleteDeptProfile = (profileId: string) => {
    void (async () => {
      const confirmed = await dialogs.confirm('是否確定要刪除部門?'); // :72（003F）
      if (!confirmed) {
        return;
      }
      const next = profiles.filter((pf) => String(pf['id']) !== profileId);
      setBusy(true);
      try {
        await dialogs.processing.wrap(dataSource.update(pageId, accountId, { profileList: next }));
        setProfiles(next);
        await dialogs.info(saveSuccessText());
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  /** 角色設定 modal 開啟（legacy showRoleSettingModal，RoleSet.jsp:6-62）。 */
  const openRoleModal = (profile: Row) => {
    void (async () => {
      // legacy roleList 由 server 端 jstl 迴圈供給（RoleSet.jsp:11-16）；雛形讀 config-role 列（既有 list op）。
      try {
        const result = await dataSource.list('config-role');
        setRoles(
          result.rows.map((roleRow) => ({
            id: String(roleRow.id),
            name: String(roleRow['category'] ?? ''),
          })),
        );
      } catch {
        setRoles([]);
      }
      const existing = Array.isArray(profile['permissionProfileRoleList'])
        ? (profile['permissionProfileRoleList'] as readonly Row[])
        : [];
      setRoleModal({
        profileId: String(profile['id'] ?? ''),
        checked: new Set(existing.map((entry) => String(entry['roleId'] ?? ''))),
      });
    })();
  };

  /** 角色設定存檔（legacy saveRole，RoleSet.jsp:70-101）。 */
  const saveRoles = () => {
    void (async () => {
      if (roleModal === null) {
        return;
      }
      const next = profiles.map((pf) => {
        if (String(pf['id']) !== roleModal.profileId) {
          return pf;
        }
        return {
          ...pf,
          permissionProfileRoleList: roles
            .filter((role) => roleModal.checked.has(role.id))
            .map((role) => ({ roleId: role.id, roleName: role.name })),
        };
      });
      setBusy(true);
      try {
        // legacy POST config/accountV2/saveRoleListAjax {roleId, permissionProfileId}（RoleSet.jsp:90）。
        await dialogs.processing.wrap(dataSource.update(pageId, accountId, { profileList: next }));
        setProfiles(next);
        setRoleModal(null); // roleSettingModalClose（RoleSet.jsp:94）；legacy 順序是 info(:93)→close(:94)，
        // 終態相同；React 先關再 info 是因 await info 會等使用者關閉，不先關子 modal 會卡住關閉動作
        await dialogs.info(saveSuccessText()); // :93
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  // ------------------------------------------------------------------
  // 證照（legacy certificationTab，accountV2tab_certification.jsp）
  // ------------------------------------------------------------------
  /** 分頁列表的「新增證書」（:200 → showEdit(null)；守衛 :73-75）。 */
  const openNewCertification = () => {
    void (async () => {
      if (accountId === '0') {
        await dialogs.alert('請先新增人員資料並存檔後，再維護證照資料'); // :74（FF0C）
        return;
      }
      setCertModal({
        id: '0', // :87
        type: '',
        name: '',
        regisDate: '',
        expireDate: '',
        memo: '',
        typeInvalid: false,
        nameInvalid: false,
      });
    })();
  };

  /** 清單名稱連結＝legacy showEdit(rowObj)（:40-44/:72-116）。 */
  const showCertification = (cert: Row) => {
    void (async () => {
      if (accountId === '0') {
        await dialogs.alert('請先新增人員資料並存檔後，再維護證照資料'); // :74
        return;
      }
      setCertModal({
        id: String(cert['id'] ?? ''),
        type: String(cert['type'] ?? ''),
        name: String(cert['name'] ?? ''),
        regisDate: String(cert['regisDateString'] ?? ''),
        expireDate: String(cert['expireDateString'] ?? ''),
        memo: String(cert['memo'] ?? ''),
        typeInvalid: false,
        nameInvalid: false,
      });
    })();
  };

  /** 證照存檔（legacy certificationTab.save，:124-163）。required 欄位以 invalid 樣式鏡像 jquery validate inline。 */
  const saveCertification = () => {
    void (async () => {
      if (certModal === null) {
        return;
      }
      const typeInvalid = certModal.type.trim() === '';
      const nameInvalid = certModal.name.trim() === '';
      setCertModal({ ...certModal, typeInvalid, nameInvalid });
      if (typeInvalid || nameInvalid) {
        return; // legacy $("#certificationForm").valid() 不過就不送（:125-127；inline 訊息無 alert）
      }
      const base: Row = {
        type: certModal.type,
        name: certModal.name,
        regisDateString: certModal.regisDate,
        expireDateString: certModal.expireDate,
        memo: certModal.memo,
        erasable: 1,
      };
      let next: readonly Row[];
      if (certModal.id === '0') {
        next = [...certs, { id: `cert-${certs.length + 1}`, sort: certs.length + 1, ...base }];
      } else {
        next = certs.map((cert) =>
          String(cert['id']) === certModal.id ? { ...cert, ...base } : cert,
        );
      }
      setBusy(true);
      try {
        // legacy POST config/certification/saveCertificationAjax（:141）；雛形存列上（update 既有 op）。
        await dialogs.processing.wrap(dataSource.update(pageId, accountId, { certifications: next }));
        setCertModal(null); // certificationSettingModalClose（:151，先關再刷新，照 legacy 順序 :151-153）
        setCerts(next); // showList 鏡像（:153）
        await dialogs.info(saveSuccessText()); // :152
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  /** 證照刪除（legacy certificationTab.del，:165-195）。 */
  const deleteCertification = (certId: string) => {
    void (async () => {
      const confirmed = await dialogs.confirm('是否確定要刪除?'); // :166（003F）
      if (!confirmed) {
        return;
      }
      const next = certs.filter((cert) => String(cert['id']) !== certId);
      setBusy(true);
      try {
        await dialogs.processing.wrap(dataSource.update(pageId, accountId, { certifications: next }));
        setCerts(next);
        await dialogs.info(saveSuccessText());
      } catch (cause: unknown) {
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  // ------------------------------------------------------------------
  // 畫面
  // ------------------------------------------------------------------
  // 批次 50（工作項 4）：key（payload key 語意）入 uiId/htmlFor 的字尾一律 toUiKey kebab 化
  // （§10.3 規則 1；key 參數本身不動＝payload 域零影響）。
  const fieldLabel = (key: string, text: string, required: boolean) => (
    <label className="shrink-0 text-sm" htmlFor={`${pageId}-account-field-${toUiKey(key)}`}>
      {required ? <span className="text-danger">*</span> : null}
      {text}
    </label>
  );

  const textInput = (
    key: string,
    value: string,
    onChange: (next: string) => void,
    options?: { maxLength?: number; readOnly?: boolean },
  ) => (
    <TextInput
      uiId={`${pageId}-account-field-${toUiKey(key)}`}
      value={value}
      onChange={onChange}
      {...(options?.maxLength !== undefined ? { maxLength: options.maxLength } : {})}
      {...(options?.readOnly === true ? { readOnly: true } : {})}
    />
  );

  const passwordInput = (key: string, value: string, onChange: (next: string) => void) => (
    // legacy type=password（accountV2.jsp:792/:798）；共用 PasswordInput 元件。
    <PasswordInput uiId={`${pageId}-account-field-${toUiKey(key)}`} value={value} onChange={onChange} />
  );

  return (
    <ModalFrame
      uiId={`${pageId}-account-dialog`}
      open
      title={mode === 'create' ? '新增帳號' : '修改帳號'} // accountV2.jsp:189/:197
      size="lg"
      onClose={closeMainModal}
      footer={
        <>
          <Button
            uiId={`${pageId}-account-dialog-footer-close`}
            variant="default"
            size="sm"
            type="button"
            onClick={closeMainModal}
          >
            {/* :857（btn-close data-dismiss） */}
            關閉
          </Button>
          <Button
            uiId={`${pageId}-account-dialog-save`}
            variant="primary"
            size="sm"
            type="button"
            disabled={busy}
            // C5-j：存檔真的呼叫 dataSource.create/update。
            dataEffect={`${accountId === '0' ? 'create' : 'update'}:${pageId}`}
            onClick={saveAccount}
          >
            {/* :860（btn-add onclick=saveAccount()） */}
            存檔
          </Button>
        </>
      }
    >
      <div data-ui-id={`${pageId}-account-dialog-body`} className="space-y-3">
        {/* 帳號主表單（accountV2.jsp:773-829）：左欄欄位＋右欄照片預覽。 */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {fieldLabel('name', '中文姓名：', true)}
              {textInput('name', name, setName, { maxLength: 200 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('englishName', '英文姓名：', false)}
              {textInput('englishName', englishName, setEnglishName, { maxLength: 200 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('loginName', '登入帳號：', true)}
              {textInput('loginName', loginName, setLoginName, { maxLength: 30 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('loginPin', '本機密碼：', true)}
              {passwordInput('loginPin', loginPin, setLoginPin)}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('loginPin_again', '確認本機密碼：', true)}
              {passwordInput('loginPin_again', loginPinAgain, setLoginPinAgain)}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('photo-file', '照片上傳：', false)}
              <input
                data-ui-id={`${pageId}-account-field-photo-file`}
                type="file"
                accept=".png, .jpg, .jpeg"
                className="text-sm"
              />
            </div>
          </div>
          <div className="flex items-center justify-center">
            {/* :827 getPhoto 預覽；雛形無真檔案服務 → placeholder【雛形值待實機校準】 */}
            <div
              data-ui-id={`${pageId}-account-photo-preview`}
              className="flex h-[180px] w-[180px] items-center justify-center border border-dashed border-gray-400 text-center text-xs text-gray-500"
            >
              照片預覽
              <br />
              （getPhoto；【雛形值待實機校準】）
            </div>
          </div>
        </div>

        {/* 分頁籤（:833-838）。 */}
        <div className="flex flex-wrap gap-1 border-b border-gray-300">
          {ACCOUNT_TABS.map((tab) => (
            <button
              key={tab.key}
              data-ui-id={`${pageId}-account-tab-${tab.key}`}
              type="button"
              className={`px-3 py-1 text-sm ${
                activeTab === tab.key ? 'border border-b-0 border-gray-300 font-medium' : ''
              }`}
              onClick={() => {
                if (tab.key === 'deptpartment') {
                  showDeptList(); // :838 onclick=departmentTab.showDeptList()
                } else {
                  setActiveTab(tab.key);
                }
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 員工資料（accountV2tab_employee.jsp）。 */}
        {activeTab === 'employee' ? (
          <div
            data-ui-id={`${pageId}-account-tab-panel-employee`}
            className="grid grid-cols-2 gap-x-4 gap-y-2"
          >
            <div className="flex items-center gap-2">
              {fieldLabel('officeMobile', '行動電話(公務)：', false)}
              {textInput('officeMobile', employeeForm.officeMobile, setFormValue(setEmployeeForm, 'officeMobile'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('officeTel', '辦公室號碼：', false)}
              {textInput('officeTel', employeeForm.officeTel, setFormValue(setEmployeeForm, 'officeTel'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('officeCode', '簡碼：', false)}
              {textInput('officeCode', employeeForm.officeCode, setFormValue(setEmployeeForm, 'officeCode'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('extNo', '辦公室分機：', false)}
              {textInput('extNo', employeeForm.extNo, setFormValue(setEmployeeForm, 'extNo'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('appCode', 'App簡碼：', false)}
              {textInput('appCode', employeeForm.appCode, setFormValue(setEmployeeForm, 'appCode'), { maxLength: 10 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('officEmail', '公務信箱：', false)}
              {textInput('officEmail', employeeForm.officEmail, setFormValue(setEmployeeForm, 'officEmail'), { maxLength: 50 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('employeeId', '員工編號：', false)}
              {textInput('employeeId', employeeForm.employeeId, setFormValue(setEmployeeForm, 'employeeId'), { maxLength: 25 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('onBoardDateString', '到職日：', false)}
              {/* employee.jsp:48-51 datepicker readonly；雛形簡化為文字輸入【雛形值待實機校準】 */}
              {textInput('onBoardDateString', employeeForm.onBoardDateString, setFormValue(setEmployeeForm, 'onBoardDateString'), { maxLength: 10 })}
            </div>
          </div>
        ) : null}

        {/* 個人資料（accountV2tab_personal.jsp）。 */}
        {activeTab === 'personal' ? (
          <div
            data-ui-id={`${pageId}-account-tab-panel-personal`}
            className="grid grid-cols-2 gap-x-4 gap-y-2"
          >
            <div className="flex items-center gap-2">
              {fieldLabel('countrycode', '國籍：', false)}
              <Select
                uiId={`${pageId}-account-field-countrycode`}
                value={personalForm.countrycode}
                onChange={setFormValue(setPersonalForm, 'countrycode')}
                options={[{ value: '1', label: '中華民國' }]}
              />
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('countryId', '身份證/居留證：', false)}
              {textInput('countryId', personalForm.countryId, setFormValue(setPersonalForm, 'countryId'), { maxLength: 50 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('gender', '性別：', false)}
              <Select
                uiId={`${pageId}-account-field-gender`}
                value={personalForm.gender}
                onChange={setFormValue(setPersonalForm, 'gender')}
                placeholder="請選擇"
                options={[
                  { value: '-1', label: '未設定' },
                  { value: '1', label: '男' },
                  { value: '0', label: '女' },
                ]}
              />
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('military', '兵役情形：', false)}
              <Select
                uiId={`${pageId}-account-field-military`}
                value={personalForm.military}
                onChange={setFormValue(setPersonalForm, 'military')}
                placeholder="請選擇"
                options={[
                  { value: '1', label: '免役' },
                  { value: '2', label: '役畢' },
                  { value: '3', label: '未役' },
                  { value: '4', label: '待役' },
                ]}
              />
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('birthdayString', '出生日期：', false)}
              {/* personal.jsp:99-102 datepicker；雛形簡化為文字輸入【雛形值待實機校準】 */}
              {textInput('birthdayString', personalForm.birthdayString, setFormValue(setPersonalForm, 'birthdayString'), { maxLength: 10 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('bloodType', '血型：', false)}
              <Select
                uiId={`${pageId}-account-field-blood-type`}
                value={personalForm.bloodType}
                onChange={setFormValue(setPersonalForm, 'bloodType')}
                placeholder="請選擇"
                options={[
                  { value: 'A', label: 'A' },
                  { value: 'B', label: 'B' },
                  { value: 'AB', label: 'AB' },
                  { value: 'O', label: 'O' },
                ]}
              />
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('postalCode', '通訊地址：', false)}
              {textInput('postalCode', personalForm.postalCode, setFormValue(setPersonalForm, 'postalCode'), { maxLength: 5 })}
              {textInput('address', personalForm.address, setFormValue(setPersonalForm, 'address'), { maxLength: 50 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('eatingHabits', '飲食習慣：', false)}
              <Select
                uiId={`${pageId}-account-field-eating-habits`}
                value={personalForm.eatingHabits}
                onChange={setFormValue(setPersonalForm, 'eatingHabits')}
                placeholder="請選擇"
                options={[
                  { value: '葷', label: '葷' },
                  { value: '素', label: '素' },
                ]}
              />
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('telephone', '個人市話號碼：', false)}
              {textInput('telephone', personalForm.telephone, setFormValue(setPersonalForm, 'telephone'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('mobile', '行動電話(私人)：', false)}
              {textInput('mobile', personalForm.mobile, setFormValue(setPersonalForm, 'mobile'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('schoolEmail', '電子信箱(私人)：', false)}
              {textInput('schoolEmail', personalForm.schoolEmail, setFormValue(setPersonalForm, 'schoolEmail'), { maxLength: 40 })}
            </div>
          </div>
        ) : null}

        {/* 證照（accountV2tab_certification.jsp）：清單＋新增證書。 */}
        {activeTab === 'certification' ? (
          <div data-ui-id={`${pageId}-account-tab-panel-certification`} className="space-y-2">
            <div>
              <Button
                uiId={`${pageId}-account-cert-new`}
                variant="primary"
                size="sm"
                type="button"
                onClick={openNewCertification}
              >
                {/* :200 */}
                新增證書
              </Button>
            </div>
            <Table
              uiId={`${pageId}-account-cert-table`}
              columns={[
                { key: 'action', header: '', align: 'center' },
                { key: 'name', header: '證照名稱', align: 'center' }, // :207
                { key: 'regis', header: '登記日期/發照日期', align: 'center' }, // :208
                { key: 'expire', header: '有效日期', align: 'center' }, // :209
                { key: 'type', header: '其他', align: 'center' }, // :210
              ]}
              rows={certs as readonly TableRow[]}
              rowKey={(cert) => String(cert['id'])}
              emptyText="查無符合的資料"
              renderCell={(cert, columnKey) => {
                if (columnKey === 'action') {
                  // :11-18 erasable==1 才有刪除（權限閘 config/account-UPDATE 雛形不重現）。
                  return String(cert['erasable'] ?? '') === '1' ? (
                    <Button
                      uiId={`${pageId}-account-cert-delete-${String(cert['id'])}`}
                      variant="danger"
                      size="sm"
                      type="button"
                      // C5-j：刪除真的 update certifications。
                      dataEffect={`update:${pageId}`}
                      onClick={() => {
                        deleteCertification(String(cert['id']));
                      }}
                    >
                      刪除
                    </Button>
                  ) : (
                    '\u00a0'
                  );
                }
                if (columnKey === 'name') {
                  return (
                    <button
                      data-ui-id={`${pageId}-account-cert-edit-${String(cert['id'])}`}
                      type="button"
                      className="underline"
                      onClick={() => {
                        showCertification(cert);
                      }}
                    >
                      {String(cert['name'] ?? '')}
                    </button>
                  );
                }
                if (columnKey === 'regis') {
                  return String(cert['regisDateString'] ?? '');
                }
                if (columnKey === 'expire') {
                  return String(cert['expireDateString'] ?? '');
                }
                if (columnKey === 'type') {
                  return String(cert['type'] ?? '');
                }
                return '\u00a0';
              }}
            />
          </div>
        ) : null}

        {/* 學籍資料（accountV2tab_school.jsp）。 */}
        {activeTab === 'school' ? (
          <div data-ui-id={`${pageId}-account-tab-panel-school`} className="space-y-2">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div className="flex items-center gap-2">
                {fieldLabel('school', '畢業學校：', false)}
                {textInput('school', schoolForm.school, setFormValue(setSchoolForm, 'school'), { maxLength: 30 })}
              </div>
              <div className="flex items-center gap-2">
                {fieldLabel('schoolAnnual', '畢業年度：', false)}
                {textInput('schoolAnnual', schoolForm.schoolAnnual, setFormValue(setSchoolForm, 'schoolAnnual'), { maxLength: 10 })}
              </div>
              <div className="flex items-center gap-2">
                {fieldLabel('schoolId', '學號：', false)}
                {textInput('schoolId', schoolForm.schoolId, setFormValue(setSchoolForm, 'schoolId'), { maxLength: 10 })}
              </div>
              <div className="flex items-center gap-2">
                {fieldLabel('teacher', '學校導師：', false)}
                {textInput('teacher', schoolForm.teacher, setFormValue(setSchoolForm, 'teacher'), { maxLength: 10 })}
              </div>
            </div>
            <div className="flex items-start gap-2">
              {fieldLabel('skill', '專業背景：', false)}
              <textarea
                data-ui-id={`${pageId}-account-field-skill`}
                rows={6}
                className="flex-1 border border-gray-300 px-2 py-1 text-sm"
                value={schoolForm.skill}
                onChange={(event) => {
                  setFormValue(setSchoolForm, 'skill')(event.target.value);
                }}
              />
            </div>
          </div>
        ) : null}

        {/* 其他（accountV2tab_other.jsp）；showAccountTabOther 雛形恆顯示（見檔頭）。 */}
        {activeTab === 'other' ? (
          <div data-ui-id={`${pageId}-account-tab-panel-other`} className="space-y-2">
            <div className="flex items-center gap-2">
              {fieldLabel('typeName', '僱用類別：', false)}
              {textInput('typeName', otherForm.typeName, setFormValue(setOtherForm, 'typeName'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('typeCode', '專科代號：', false)}
              {textInput('typeCode', otherForm.typeCode, setFormValue(setOtherForm, 'typeCode'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('licenseLevel', '專科類別1：', false)}
              {textInput('licenseLevel', otherForm.licenseLevel, setFormValue(setOtherForm, 'licenseLevel'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('drugLicense', '專科類別2：', false)}
              {textInput('drugLicense', otherForm.drugLicense, setFormValue(setOtherForm, 'drugLicense'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('licenseNot5', '次專科：', false)}
              {textInput('licenseNot5', otherForm.licenseNot5, setFormValue(setOtherForm, 'licenseNot5'), { maxLength: 20 })}
            </div>
            <div className="flex items-center gap-2">
              {fieldLabel('officeLocation', '辦公室位址：', true)}
              <Select
                uiId={`${pageId}-account-field-office-location`}
                value={otherForm.officeLocation}
                onChange={setFormValue(setOtherForm, 'officeLocation')}
                placeholder="請選擇"
                options={ACCOUNT_OFFICE_LOCATION_OPTIONS}
              />
            </div>
          </div>
        ) : null}

        {/* 部門職稱（accountV2tab_deptpartment.jsp）。 */}
        {activeTab === 'deptpartment' ? (
          <div data-ui-id={`${pageId}-account-tab-panel-deptpartment`} className="space-y-2">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div className="flex items-center gap-2">
                {fieldLabel('organizationIdX', '醫院：', true)}
                <Select
                  uiId={`${pageId}-account-field-organization-id-x`}
                  value={deptForm.organizationId}
                  onChange={setFormValue(setDeptForm, 'organizationId')}
                  placeholder="請選擇"
                  options={ACCOUNT_ORGANIZATION_OPTIONS}
                />
              </div>
              <div className="flex items-center gap-2">
                {fieldLabel('jobTypeIdX', '訓練類別：', true)}
                <Select
                  uiId={`${pageId}-account-field-job-type-id-x`}
                  value={deptForm.jobTypeId}
                  onChange={setFormValue(setDeptForm, 'jobTypeId')}
                  placeholder="請選擇"
                  options={ACCOUNT_JOB_TYPE_OPTIONS}
                />
              </div>
              <div className="flex items-center gap-2">
                {fieldLabel('departmentIdX', '部門：', true)}
                <Select
                  uiId={`${pageId}-account-field-department-id-x`}
                  value={deptForm.departmentId}
                  onChange={setFormValue(setDeptForm, 'departmentId')}
                  placeholder="請選擇"
                  options={ACCOUNT_DEPARTMENT_OPTIONS}
                />
              </div>
              <div className="flex items-center gap-2">
                {fieldLabel('jobTitleIdX', '職稱：', true)}
                <Select
                  uiId={`${pageId}-account-field-job-title-id-x`}
                  value={deptForm.jobTitleId}
                  onChange={setFormValue(setDeptForm, 'jobTitleId')}
                  placeholder="請選擇"
                  options={ACCOUNT_JOB_TITLE_OPTIONS}
                />
              </div>
            </div>
            <div className="flex gap-2">
              {showDeptNew ? (
                <Button
                  uiId={`${pageId}-account-dept-save-new`}
                  variant="primary"
                  size="sm"
                  type="button"
                  // C5-j：真的 update profileList。
                  dataEffect={`update:${pageId}`}
                  onClick={() => {
                    saveDeptProfile(false);
                  }}
                >
                  {/* :171 */}
                  新增部門設定
                </Button>
              ) : null}
              {deptShowChange ? (
                <Button
                  uiId={`${pageId}-account-dept-save-change`}
                  variant="default"
                  size="sm"
                  type="button"
                  dataEffect={`update:${pageId}`}
                  onClick={() => {
                    saveDeptProfile(true); // legacy savePermissionProfile(-1)：沿用現 profileId
                  }}
                >
                  {/* :172 */}
                  修改部門設定
                </Button>
              ) : null}
            </div>
            <Table
              uiId={`${pageId}-account-dept-table`}
              columns={[
                { key: 'action', header: '', align: 'center' },
                { key: 'organizationName', header: '醫院', align: 'center' }, // spring organization（properties:122）
                { key: 'jobTypeName', header: '訓練類別', align: 'center' }, // spring job.type（properties:97）
                { key: 'departmentName', header: '部門', align: 'center' }, // spring department（properties:60）
                { key: 'jobTitleName', header: '職稱', align: 'center' }, // spring job.title（properties:96）
                { key: 'role', header: '角色設定', align: 'center' }, // spring role.config（properties:147）
              ]}
              rows={profiles as readonly TableRow[]}
              rowKey={(profile) => String(profile['id'])}
              emptyText="查無符合的資料"
              renderCell={(profile, columnKey) => {
                if (columnKey === 'action') {
                  return (
                    <span className="inline-flex gap-1">
                      <Button
                        uiId={`${pageId}-account-dept-edit-${String(profile['id'])}`}
                        variant="default"
                        size="sm"
                        type="button"
                        onClick={() => {
                          showChangePermissionProfile(profile);
                        }}
                      >
                        修改
                      </Button>
                      <Button
                        uiId={`${pageId}-account-dept-delete-${String(profile['id'])}`}
                        variant="danger"
                        size="sm"
                        type="button"
                        dataEffect={`update:${pageId}`}
                        onClick={() => {
                          deleteDeptProfile(String(profile['id']));
                        }}
                      >
                        刪除
                      </Button>
                    </span>
                  );
                }
                if (columnKey === 'role') {
                  return (
                    <Button
                      uiId={`${pageId}-account-dept-role-${String(profile['id'])}`}
                      variant="default"
                      size="sm"
                      type="button"
                      onClick={() => {
                        openRoleModal(profile); // :207 showRoleSettingModal({{id}})
                      }}
                    >
                      角色設定
                    </Button>
                  );
                }
                return String(profile[columnKey] ?? '');
              }}
            />
          </div>
        ) : null}
      </div>

      {/* 證照 modal（accountV2tab_certification_modal.jsp:231-308；疊放在主 modal 上，進度檔 D7）。 */}
      {certModal !== null ? (
        <ModalFrame
          uiId={`${pageId}-account-cert-modal`}
          open
          title={certModal.id === '0' ? '新增證書' : '修改證書'} // :86/:89
          size="lg"
          onClose={() => {
            setCertModal(null); // certificationSettingModalClose（:118-122）
          }}
          footer={
            <>
              <Button
                uiId={`${pageId}-account-cert-modal-footer-close`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  setCertModal(null);
                }}
              >
                {/* :301 */}
                關閉
              </Button>
              <Button
                uiId={`${pageId}-account-cert-modal-save`}
                variant="primary"
                size="sm"
                type="button"
                disabled={busy}
                dataEffect={`update:${pageId}`}
                onClick={saveCertification}
              >
                {/* :303 */}
                存檔
              </Button>
            </>
          }
        >
          <div data-ui-id={`${pageId}-account-cert-modal-body`} className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="w-40 shrink-0 text-sm" htmlFor={`${pageId}-account-cert-field-type`}>
                <span className="text-danger">*</span>證照類別：{/* :243 */}
              </label>
              <Select
                uiId={`${pageId}-account-cert-field-type`}
                value={certModal.type}
                invalid={certModal.typeInvalid}
                onChange={(next) => {
                  setCertModal({ ...certModal, type: next, typeInvalid: false });
                }}
                placeholder="請選擇"
                options={ACCOUNT_CERTIFICATE_TYPE_OPTIONS}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-40 shrink-0 text-sm" htmlFor={`${pageId}-account-cert-field-name`}>
                <span className="text-danger">*</span>證照名稱：{/* :250 */}
              </label>
              <TextInput
                uiId={`${pageId}-account-cert-field-name`}
                value={certModal.name}
                invalid={certModal.nameInvalid}
                maxLength={50}
                onChange={(next) => {
                  setCertModal({ ...certModal, name: next, nameInvalid: false });
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-40 shrink-0 text-sm" htmlFor={`${pageId}-account-cert-field-regis-date`}>
                登記或發照日期：{/* :256 */}
              </label>
              {/* :258-261 datepicker；雛形簡化為文字輸入【雛形值待實機校準】 */}
              <TextInput
                uiId={`${pageId}-account-cert-field-regis-date`}
                value={certModal.regisDate}
                maxLength={10}
                onChange={(next) => {
                  setCertModal({ ...certModal, regisDate: next });
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-40 shrink-0 text-sm" htmlFor={`${pageId}-account-cert-field-expire-date`}>
                有效日期：{/* :265 */}
              </label>
              <TextInput
                uiId={`${pageId}-account-cert-field-expire-date`}
                value={certModal.expireDate}
                maxLength={10}
                onChange={(next) => {
                  setCertModal({ ...certModal, expireDate: next });
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-40 shrink-0 text-sm" htmlFor={`${pageId}-account-cert-field-certification-front`}>
                {/* certification_modal.jsp:59（半形括號 0028/0029＋全形冒號 FF1A） */}
                證照上傳(正)：
              </label>
              <input
                data-ui-id={`${pageId}-account-cert-field-certification-front`}
                type="file"
                accept=".png, .jpg, .jpeg"
                className="text-sm"
              />
              <div
                data-ui-id={`${pageId}-account-cert-front-preview`}
                className="flex h-[120px] w-[120px] items-center justify-center border border-dashed border-gray-400 text-xs text-gray-500"
              >
                正面預覽（download）
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-40 shrink-0 text-sm" htmlFor={`${pageId}-account-cert-field-certification-back`}>
                {/* certification_modal.jsp:68 */}
                證照上傳(反)：
              </label>
              <input
                data-ui-id={`${pageId}-account-cert-field-certification-back`}
                type="file"
                accept=".png, .jpg, .jpeg"
                className="text-sm"
              />
              <div
                data-ui-id={`${pageId}-account-cert-back-preview`}
                className="flex h-[120px] w-[120px] items-center justify-center border border-dashed border-gray-400 text-xs text-gray-500"
              >
                反面預覽（download）
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-40 shrink-0 text-sm" htmlFor={`${pageId}-account-cert-field-memo`}>
                備註：{/* :292 */}
              </label>
              <TextInput
                uiId={`${pageId}-account-cert-field-memo`}
                value={certModal.memo}
                maxLength={30}
                onChange={(next) => {
                  setCertModal({ ...certModal, memo: next });
                }}
              />
            </div>
          </div>
        </ModalFrame>
      ) : null}

      {/* 角色設定 modal（accountV2RoleSet.jsp:103-134；疊放在主 modal 上，進度檔 D7）。 */}
      {roleModal !== null ? (
        <ModalFrame
          uiId={`${pageId}-account-role-modal`}
          open
          title="角色設定" // RoleSet.jsp:108
          size="lg"
          onClose={() => {
            setRoleModal(null); // roleSettingModalClose（RoleSet.jsp:64-68）
          }}
          footer={
            <>
              <Button
                uiId={`${pageId}-account-role-modal-footer-close`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  setRoleModal(null);
                }}
              >
                {/* :128 */}
                關閉
              </Button>
              <Button
                uiId={`${pageId}-account-role-modal-save`}
                variant="primary"
                size="sm"
                type="button"
                disabled={busy}
                dataEffect={`update:${pageId}`}
                onClick={saveRoles}
              >
                {/* :130 */}
                存檔
              </Button>
            </>
          }
        >
          <div data-ui-id={`${pageId}-account-role-modal-body`}>
            {/* RoleSet.jsp:19-27：4 顆/列的 checkbox 表。 */}
            <table
              data-ui-id={`${pageId}-account-role-table`}
              className="w-full border border-gray-300 text-sm"
            >
              <tbody>
                {chunk(roles, 4).map((group, groupIndex) => (
                  <tr key={`role-row-${groupIndex}`} data-ui-id={`${pageId}-account-role-row-${groupIndex}`}>
                    {group.map((role) => (
                      <td key={role.id} className="border border-gray-200 px-2 py-1">
                        <label className="inline-flex items-center gap-1">
                          <input
                            data-ui-id={`${pageId}-account-role-check-${role.id}`}
                            type="checkbox"
                            checked={roleModal.checked.has(role.id)}
                            onChange={() => {
                              const next = new Set(roleModal.checked);
                              if (next.has(role.id)) {
                                next.delete(role.id);
                              } else {
                                next.add(role.id);
                              }
                              setRoleModal({ ...roleModal, checked: next });
                            }}
                          />
                          {role.name}
                        </label>
                      </td>
                    ))}
                    {group.length < 4
                      ? Array.from({ length: 4 - group.length }, (_, padIndex) => (
                          <td key={`pad-${padIndex}`} className="border border-gray-200 px-2 py-1">
                            {'\u00a0'}
                          </td>
                        ))
                      : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModalFrame>
      ) : null}
    </ModalFrame>
  );
}

/** 4 顆/列的切組（RoleSet.jsp:19-27 i%4）。 */
function chunk<T>(items: readonly T[], size: number): readonly T[][] {
  const groups: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    groups.push(items.slice(index, index + size));
  }
  return groups;
}
